(() => {
  const SECTION_SELECTOR = '[data-gallery-section]';
  const DEFAULT_FILTER = 'all';

  function normalizeFilter(value) {
    return typeof value === 'string'
      ? value.trim().toLowerCase()
      : '';
  }

  function isRegularClick(event) {
    return (
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey
    );
  }

  function numericValue(value, fallback = 9999) {
    const number = Number(value);

    return Number.isFinite(number)
      ? number
      : fallback;
  }

  function getItemCategories(item) {
    return new Set(
      (item.dataset.categories || '')
        .split(',')
        .map(normalizeFilter)
        .filter(Boolean)
    );
  }

  function sortFilters(container) {
    const filters = Array.from(
      container.querySelectorAll('[data-gallery-filter]')
    );

    filters
      .sort((first, second) => {
        return (
          numericValue(first.dataset.sort) -
          numericValue(second.dataset.sort)
        );
      })
      .forEach((filter) => {
        container.appendChild(filter);
      });
  }

  function sortGalleryItems(grid) {
    const items = Array.from(
      grid.querySelectorAll('[data-gallery-item]')
    );

    items
      .sort((first, second) => {
        const categoryDifference =
          numericValue(first.dataset.categorySort) -
          numericValue(second.dataset.categorySort);

        if (categoryDifference !== 0) {
          return categoryDifference;
        }

        return (
          numericValue(first.dataset.imageSort) -
          numericValue(second.dataset.imageSort)
        );
      })
      .forEach((item) => {
        grid.appendChild(item);
      });
  }

  /**
   * The same Shopify image can be selected in several categories.
   * This merges duplicate cards and combines their categories.
   */
  function mergeDuplicateImages(grid) {
    const items = Array.from(
      grid.querySelectorAll('[data-gallery-item]')
    );

    const uniqueItems = new Map();

    items.forEach((item) => {
      const link = item.querySelector('[data-gallery-link]');

      const imageKey =
        item.dataset.imageKey ||
        link?.href ||
        '';

      if (!imageKey) {
        return;
      }

      const existingItem = uniqueItems.get(imageKey);

      if (!existingItem) {
        uniqueItems.set(imageKey, item);
        return;
      }

      const existingCategories =
        getItemCategories(existingItem);

      const duplicateCategories =
        getItemCategories(item);

      duplicateCategories.forEach((category) => {
        existingCategories.add(category);
      });

      existingItem.dataset.categories =
        Array.from(existingCategories).join(',');

      item.remove();
    });

    return Array.from(uniqueItems.values());
  }

  function initGallery(section) {
    if (!(section instanceof HTMLElement)) {
      return;
    }

    if (section.dataset.galleryInitialized === 'true') {
      return;
    }

    const filtersContainer = section.querySelector(
      '[data-gallery-filters]'
    );

    const grid = section.querySelector(
      '[data-gallery-grid]'
    );

    const emptyMessage = section.querySelector(
      '[data-gallery-empty]'
    );

    if (
      !(filtersContainer instanceof HTMLElement) ||
      !(grid instanceof HTMLElement)
    ) {
      return;
    }

    section.dataset.galleryInitialized = 'true';

    sortFilters(filtersContainer);
    sortGalleryItems(grid);

    const galleryItems = mergeDuplicateImages(grid);

    const filterLinks = Array.from(
      filtersContainer.querySelectorAll(
        '[data-gallery-filter]'
      )
    );

    const queryParam =
      section.dataset.queryParam || 'category';

    const allowedFilters = new Set(
      filterLinks
        .map((link) => {
          return normalizeFilter(
            link.dataset.galleryFilter
          );
        })
        .filter(Boolean)
    );

    const abortController = new AbortController();
    const { signal } = abortController;

    section.galleryAbortController = abortController;

    function createFilterUrl(filter) {
      const url = new URL(window.location.href);

      if (filter === DEFAULT_FILTER) {
        url.searchParams.delete(queryParam);
      } else {
        url.searchParams.set(queryParam, filter);
      }

      return url;
    }

    function syncFilterLinks() {
      filterLinks.forEach((link) => {
        const filter = normalizeFilter(
          link.dataset.galleryFilter
        );

        if (!filter) {
          return;
        }

        const url = createFilterUrl(filter);

        link.href =
          `${url.pathname}${url.search}${url.hash}`;
      });
    }

    function getFilterFromUrl() {
      const url = new URL(window.location.href);

      const requestedFilter = normalizeFilter(
        url.searchParams.get(queryParam)
      );

      if (!requestedFilter) {
        return {
          filter: DEFAULT_FILTER,
          isValid: true
        };
      }

      if (!allowedFilters.has(requestedFilter)) {
        return {
          filter: DEFAULT_FILTER,
          isValid: false
        };
      }

      return {
        filter: requestedFilter,
        isValid: true
      };
    }

    function updateUrl(filter, mode = 'push') {
      const url = createFilterUrl(filter);

      const relativeUrl =
        `${url.pathname}${url.search}${url.hash}`;

      const state = {
        ...(window.history.state || {}),
        galleryFilter: filter
      };

      if (mode === 'replace') {
        window.history.replaceState(
          state,
          '',
          relativeUrl
        );
      } else {
        window.history.pushState(
          state,
          '',
          relativeUrl
        );
      }

      syncFilterLinks();
    }

    function itemMatchesFilter(item, filter) {
      if (filter === DEFAULT_FILTER) {
        return true;
      }

      return getItemCategories(item).has(filter);
    }

    function applyFilter(requestedFilter) {
      const filter = allowedFilters.has(
        requestedFilter
      )
        ? requestedFilter
        : DEFAULT_FILTER;

      let visibleItemsCount = 0;

      filterLinks.forEach((link) => {
        const linkFilter = normalizeFilter(
          link.dataset.galleryFilter
        );

        const isActive = linkFilter === filter;

        link.classList.toggle(
          'is-active',
          isActive
        );

        if (isActive) {
          link.setAttribute(
            'aria-current',
            'page'
          );
        } else {
          link.removeAttribute('aria-current');
        }
      });

      galleryItems.forEach((item) => {
        const isVisible = itemMatchesFilter(
          item,
          filter
        );

        item.hidden = !isVisible;

        if (isVisible) {
          visibleItemsCount += 1;
        }
      });

      grid.dataset.activeFilter = filter;

      if (emptyMessage instanceof HTMLElement) {
        emptyMessage.hidden =
          visibleItemsCount > 0;
      }
    }

    function getVisibleGalleryLinks() {
      return galleryItems
        .filter((item) => !item.hidden)
        .map((item) => {
          return item.querySelector(
            '[data-gallery-link]'
          );
        })
        .filter((link) => {
          return link instanceof HTMLAnchorElement;
        });
    }

    filtersContainer.addEventListener(
      'click',
      (event) => {
        const clickedFilter = event.target.closest(
          '[data-gallery-filter]'
        );

        if (
          !(clickedFilter instanceof HTMLAnchorElement) ||
          !isRegularClick(event)
        ) {
          return;
        }

        const filter = normalizeFilter(
          clickedFilter.dataset.galleryFilter
        );

        if (!allowedFilters.has(filter)) {
          return;
        }

        event.preventDefault();

        applyFilter(filter);
        updateUrl(filter);
      },
      { signal }
    );

    grid.addEventListener(
      'click',
      (event) => {
        const clickedLink = event.target.closest(
          '[data-gallery-link]'
        );

        if (
          !(clickedLink instanceof HTMLAnchorElement) ||
          !isRegularClick(event)
        ) {
          return;
        }

        const parentItem = clickedLink.closest(
          '[data-gallery-item]'
        );

        if (
          !(parentItem instanceof HTMLElement) ||
          parentItem.hidden
        ) {
          return;
        }

        /*
         * If Fancybox has not loaded, the normal link
         * remains usable and opens the large image.
         */
        if (
          !window.Fancybox ||
          typeof window.Fancybox.show !== 'function'
        ) {
          return;
        }

        event.preventDefault();

        const visibleLinks =
          getVisibleGalleryLinks();

        const startIndex =
          visibleLinks.indexOf(clickedLink);

        if (startIndex < 0) {
          return;
        }

        const slides = visibleLinks.map((link) => {
          const previewImage =
            link.querySelector('img');

          return {
            src: link.href,
            type: 'image',
            caption: link.dataset.caption || '',
            thumbSrc:
              previewImage?.currentSrc ||
              previewImage?.src ||
              ''
          };
        });

        window.Fancybox.show(slides, {
          startIndex,
          theme: 'dark',
          closeExisting: true,
          triggerEl: clickedLink,
          triggerEvent: event,
          zoomEffect: true
        });
      },
      { signal }
    );

    window.addEventListener(
      'popstate',
      () => {
        const { filter } = getFilterFromUrl();

        applyFilter(filter);
        syncFilterLinks();
      },
      { signal }
    );

    const initialFilter = getFilterFromUrl();

    applyFilter(initialFilter.filter);
    syncFilterLinks();

    if (!initialFilter.isValid) {
      updateUrl(DEFAULT_FILTER, 'replace');
    }
  }

  function initAllGalleries(scope = document) {
    if (
      scope instanceof HTMLElement &&
      scope.matches(SECTION_SELECTOR)
    ) {
      initGallery(scope);
    }

    scope
      .querySelectorAll(SECTION_SELECTOR)
      .forEach((section) => {
        initGallery(section);
      });
  }

  function start() {
    initAllGalleries(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAllGalleries(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const section = event.target.matches?.(
        SECTION_SELECTOR
      )
        ? event.target
        : event.target.querySelector?.(
            SECTION_SELECTOR
          );

      if (
        section?.galleryAbortController instanceof
        AbortController
      ) {
        section.galleryAbortController.abort();
      }
    }
  );
})();